<script>
  import { onDestroy, onMount } from 'svelte';
  import { AlertCircle, Camera, Check, LoaderCircle, Search, UserPlus, UserRound, Users, X } from 'lucide-svelte';
  import {
    activateConversation,
    conversations,
    createGroupConversationWS,
    searchUsersWS
  } from '$lib/websocket-client.js';
  import { avatarContentUrl } from '$lib/avatar';
  import MessagePreview from '@components/messenger/MessagePreview.svelte';

  export let onClose = () => {};
  export let onCreated = () => {};
  export let showTitle = true;

  const filters = [
    { id: 'all', label: 'Tất cả' },
    { id: 'recent', label: 'Trò chuyện gần đây' },
    { id: 'system', label: 'Thêm user' }
  ];

  let groupName = '';
  let keyword = '';
  let activeFilter = 'all';
  let selectedUsers = new Map();
  let systemUsers = [];
  let loadingSystemUsers = false;
  let creating = false;
  let error = '';
  let systemError = '';
  let debounceTimer;
  let searchSequence = 0;
  let lastSearchKey = '';

  $: directContacts = ($conversations || [])
    .filter((item) => item?.type === 'direct' && item?.userId)
    .reduce((items, item) => {
      if (items.some((contact) => String(contact.id) === String(item.userId))) return items;

      items.push({
        id: item.userId,
        name: item.title || 'Người dùng',
        avatar: item.avatar,
        online: item.online,
        source: 'recent',
        subtitle: item.lastMessagePreview || '',
        conversationId: item.conversationId
      });

      return items;
    }, []);

  $: existingDirectUserIds = new Set(directContacts.map((item) => String(item.id)));
  $: normalizedKeyword = keyword.trim().toLowerCase();
  $: newSystemUsers = systemUsers
    .filter((user) => !existingDirectUserIds.has(String(user.id)))
    .map((user) => ({
      ...user,
      source: 'system',
      subtitle: user.email || user.phone || 'User toàn hệ thống'
    }));
  $: allUsers = [...directContacts, ...newSystemUsers].filter(
    (user, index, items) => items.findIndex((item) => String(item.id) === String(user.id)) === index
  );
  $: sourceUsers = activeFilter === 'recent' ? directContacts : activeFilter === 'system' ? newSystemUsers : allUsers;
  $: visibleUsers = sourceUsers.filter((user) => {
    if (!normalizedKeyword) return true;

    return [user.name, user.subtitle, user.email, user.phone].some((value) =>
      String(value || '')
        .toLowerCase()
        .includes(normalizedKeyword)
    );
  });
  $: selectedList = Array.from(selectedUsers.values());
  $: canCreate = groupName.trim().length >= 2 && selectedUsers.size >= 2 && !creating;
  $: searchKey = `${activeFilter}|${keyword.trim()}`;
  $: if (searchKey !== lastSearchKey) {
    lastSearchKey = searchKey;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runSystemSearch(), 320);
  }

  onMount(() => {
    lastSearchKey = searchKey;
    runSystemSearch();
  });

  onDestroy(() => {
    clearTimeout(debounceTimer);
    searchSequence += 1;
  });

  function getInitial(name = '') {
    return (
      String(name || '?')
        .trim()
        .charAt(0)
        .toUpperCase() || '?'
    );
  }

  async function runSystemSearch() {
    const searchKeyword = keyword.trim();
    const seq = ++searchSequence;

    if (activeFilter === 'recent') {
      loadingSystemUsers = false;
      systemError = '';
      return;
    }

    loadingSystemUsers = true;
    systemError = '';

    try {
      const rows = await searchUsersWS({
        keyword: searchKeyword,
        scope: 'system',
        limit: 40,
        excludeExisting: true
      });

      if (seq !== searchSequence) return;
      systemUsers = rows;
    } catch (err) {
      if (seq !== searchSequence) return;
      systemUsers = [];
      systemError = err?.message || 'Không thể tìm user toàn hệ thống.';
    } finally {
      if (seq === searchSequence) {
        loadingSystemUsers = false;
      }
    }
  }

  function toggleUser(user) {
    if (!user?.id || creating) return;

    const next = new Map(selectedUsers);

    if (next.has(user.id)) {
      next.delete(user.id);
    } else {
      next.set(user.id, user);
    }

    selectedUsers = next;
  }

  function removeUser(user) {
    if (!user?.id || creating) return;

    const next = new Map(selectedUsers);
    next.delete(user.id);
    selectedUsers = next;
  }

  async function createGroup() {
    if (!canCreate) return;

    try {
      creating = true;
      error = '';

      const conversation = await createGroupConversationWS({
        name: groupName,
        memberIds: selectedList.map((user) => user.id)
      });

      await activateConversation(conversation);
      onCreated?.(conversation);
      onClose?.();
    } catch (err) {
      error = err?.message || 'Không thể tạo nhóm.';
    } finally {
      creating = false;
    }
  }
</script>

<section
  class="flex min-h-0 max-h-[88dvh] w-full flex-col overflow-hidden rounded-2xl bg-white text-slate-900 shadow-xl sm:max-h-[720px]">
  {#if showTitle}
    <header class="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
      <h3 class="text-xl font-semibold text-slate-950 sm:text-2xl">Tạo nhóm</h3>

      <button
        type="button"
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-900 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100"
        aria-label="Đóng"
        onclick={onClose}>
        <X size={24} strokeWidth={2} />
      </button>
    </header>
  {/if}

  <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
    <div class="space-y-4 border-b border-slate-200 px-5 py-4 sm:px-6">
      <div class="flex min-w-0 items-center gap-3">
        <button
          type="button"
          class="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50"
          aria-label="Chọn ảnh nhóm">
          <Camera size={22} />
        </button>

        <input
          bind:value={groupName}
          maxlength="80"
          placeholder="Nhập tên nhóm"
          class="h-11 min-w-0 flex-1 border-0 border-b border-slate-200 bg-transparent px-0 text-sm text-slate-950 outline-none placeholder:text-sm placeholder:text-slate-400 focus:border-blue-600 focus:ring-0 sm:text-base" />
      </div>

      <div class="relative">
        <Search size={18} class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          bind:value={keyword}
          type="search"
          autocomplete="off"
          placeholder="Nhập tên, email, số điện thoại"
          class="h-12 w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 shadow-sm outline-none placeholder:text-sm placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:text-base" />
      </div>

      <div class="flex gap-3 overflow-x-auto pb-1">
        {#each filters as item}
          <button
            type="button"
            class="shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition sm:text-sm {activeFilter === item.id
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}"
            onclick={() => (activeFilter = item.id)}>
            {item.label}
          </button>
        {/each}
      </div>
    </div>

    {#if error}
      <div
        class="mx-6 mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        <AlertCircle size={16} class="mt-0.5 shrink-0" />
        <span>{error}</span>
      </div>
    {/if}

    {#if systemError && activeFilter !== 'recent'}
      <div
        class="mx-6 mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        <AlertCircle size={16} class="mt-0.5 shrink-0" />
        <span>{systemError}</span>
      </div>
    {/if}

    <div class="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[minmax(0,1fr)_368px]">
      <div class="min-w-0 min-h-0 overflow-y-auto px-5 py-4 sm:px-6">
        <h4 class="mb-3 text-base font-semibold text-slate-950 sm:text-lg">
          {activeFilter === 'system' ? 'User toàn hệ thống' : 'Trò chuyện gần đây'}
        </h4>

        {#if loadingSystemUsers && activeFilter === 'system'}
          <div class="flex h-44 flex-col items-center justify-center gap-3 text-sm text-slate-500">
            <LoaderCircle size={20} class="animate-spin text-blue-600" />
            Đang tải danh sách user...
          </div>
        {:else if visibleUsers.length}
          <div class="space-y-1">
            {#each visibleUsers as user (user.id)}
              <button
                type="button"
                class="flex w-full min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={creating}
                onclick={() => toggleUser(user)}>
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition {selectedUsers.has(
                    user.id
                  )
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-300 bg-white text-transparent'}">
                  <Check size={16} strokeWidth={3} />
                </span>

                <span
                  class="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-sky-50 text-sky-600">
                  {#if avatarContentUrl(user.avatar)}
                    <img
                      src={avatarContentUrl(user.avatar)}
                      alt={user.name}
                      class="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async" />
                  {:else}
                    <UserRound size={23} strokeWidth={2.1} />
                    <span class="sr-only">{getInitial(user.name)}</span>
                  {/if}

                  {#if user.online}
                    <span
                      class="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500">
                    </span>
                  {/if}
                </span>

                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-medium text-slate-950 sm:text-base">{user.name}</span>
                  {#if user.source === 'recent'}
                    <MessagePreview preview={user.subtitle} className="add-group-preview" />
                  {:else}
                    <span class="block truncate text-xs text-slate-500 sm:text-sm">{user.subtitle}</span>
                  {/if}
                </span>

                {#if user.source === 'system'}
                  <span
                    class="hidden rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700 sm:inline-flex">
                    Mới
                  </span>
                {/if}
              </button>
            {/each}
          </div>
        {:else}
          <div
            class="flex h-52 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center">
            {#if activeFilter === 'system'}
              <UserPlus size={22} class="text-slate-400" />
              <p class="text-sm font-medium text-slate-700">Không tìm thấy user mới</p>
              <p class="text-xs text-slate-500">User đã có direct conversation sẽ không xuất hiện ở danh sách này.</p>
            {:else}
              <Users size={22} class="text-slate-400" />
              <p class="text-sm font-medium text-slate-700">Không có liên hệ phù hợp</p>
              <p class="text-xs text-slate-500">Chọn “Thêm user” để tìm trong toàn hệ thống.</p>
            {/if}
          </div>
        {/if}
      </div>

      <aside class="hidden min-h-0 border-l border-slate-200 px-5 py-4 lg:block">
        <div class="h-full min-h-0 rounded-xl border border-slate-200 bg-white p-5">
          <div class="mb-4 flex items-center gap-3">
            <h4 class="text-base font-semibold text-slate-950">Đã chọn</h4>
            <span class="rounded-lg bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">
              {selectedUsers.size}/100
            </span>
          </div>

          {#if selectedList.length}
            <div class="space-y-2">
              {#each selectedList as user (user.id)}
                <button
                  type="button"
                  class="flex w-full min-w-0 items-center gap-3 rounded-full bg-blue-50 py-2 pl-2 pr-3 text-left text-blue-800 transition hover:bg-blue-100"
                  onclick={() => removeUser(user)}>
                  <span
                    class="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white text-sky-600">
                    {#if avatarContentUrl(user.avatar)}
                      <img
                        src={avatarContentUrl(user.avatar)}
                        alt={user.name}
                        class="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async" />
                    {:else}
                      <UserRound size={18} strokeWidth={2.1} />
                      <span class="sr-only">{getInitial(user.name)}</span>
                    {/if}
                  </span>
                  <span class="min-w-0 flex-1 truncate text-base font-medium">{user.name}</span>
                  <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-700 text-white">
                    <X size={16} />
                  </span>
                </button>
              {/each}
            </div>
          {:else}
            <div
              class="flex h-52 items-center justify-center rounded-xl border border-dashed border-slate-200 text-center text-sm text-slate-500">
              Chọn ít nhất 2 thành viên để tạo nhóm.
            </div>
          {/if}
        </div>
      </aside>
    </div>

    {#if selectedList.length}
      <div
        class="flex-none border-t border-slate-200 px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:hidden">
        <div class="flex gap-2 overflow-x-auto pb-1">
          {#each selectedList as user (user.id)}
            <button
              type="button"
              class="inline-flex shrink-0 items-center gap-2 rounded-full bg-blue-50 py-1 pl-1 pr-2 text-sm font-medium text-blue-800"
              onclick={() => removeUser(user)}>
              <span class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white text-xs">
                {#if avatarContentUrl(user.avatar)}
                  <img
                    src={avatarContentUrl(user.avatar)}
                    alt={user.name}
                    class="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async" />
                {:else}
                  <UserRound size={17} strokeWidth={2.1} />
                  <span class="sr-only">{getInitial(user.name)}</span>
                {/if}
              </span>
              <span class="max-w-[132px] truncate">{user.name}</span>
              <X size={14} />
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <footer
    class="flex shrink-0 items-center justify-end gap-4 border-t border-slate-200 bg-white px-6 py-5 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-5">
    <button
      type="button"
      class="h-12 rounded-lg bg-slate-200 px-8 text-lg font-semibold text-slate-900 transition hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
      onclick={onClose}>
      Hủy
    </button>

    <button
      type="button"
      class="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-blue-600 px-8 text-lg font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      disabled={!canCreate}
      onclick={createGroup}>
      {#if creating}
        <LoaderCircle size={20} class="animate-spin" />
        Đang tạo
      {:else}
        Tạo nhóm
      {/if}
    </button>
  </footer>
</section>

<style>
  :global(.add-group-preview) {
    margin-top: 0.22rem;
    max-width: 100%;
    padding: 0;
    font-size: 0.82rem;
  }

  :global(.add-group-preview .preview-copy) {
    flex-wrap: nowrap;
  }

  :global(.add-group-preview .preview-secondary) {
    min-width: 0;
  }
</style>
